/**
 * Node icon utilities for displaying n8n node icons
 * Supports three icon sources:
 * 1. Simple Icons CDN (for popular third-party integrations)
 * 2. n8n Native SVGs (from n8n GitHub repo)
 * 3. Font Awesome icons (via react-icons)
 */

import {
  FaArrowRight,
  FaBalanceScaleLeft,
  FaBars,
  FaBoxOpen,
  FaBrain,
  FaBug,
  FaCalculator,
  FaCalendar,
  FaCheckDouble,
  FaClock,
  FaCloudDownloadAlt,
  FaCode,
  FaComments,
  FaCut,
  FaDatabase,
  FaEdit,
  FaEnvelope,
  FaExchangeAlt,
  FaExclamationTriangle,
  FaFileArchive,
  FaFileCode,
  FaFileExport,
  FaFileImport,
  FaFilePdf,
  FaFilter,
  FaFingerprint,
  FaGripLinesVertical,
  FaHourglass,
  FaImage,
  FaInbox,
  FaKey,
  FaLink,
  FaMapSigns,
  FaMousePointer,
  FaNetworkWired,
  FaPauseCircle,
  FaPen,
  FaProjectDiagram,
  FaRobot,
  FaRss,
  FaServer,
  FaSignInAlt,
  FaSignOutAlt,
  FaStickyNote,
  FaSync,
  FaTable,
  FaTags,
  FaTasks,
  FaTerminal,
  FaThLarge,
  FaTools,
} from 'react-icons/fa';
import { FiClock as FaTimer } from 'react-icons/fi';
import type { IconType } from 'react-icons';

/**
 * Map Font Awesome icon names to react-icons components
 */
export const FA_ICON_MAP: Record<string, IconType> = {
  'arrow-right': FaArrowRight,
  'balance-scale-left': FaBalanceScaleLeft,
  'bars': FaBars,
  'box-open': FaBoxOpen,
  'brain': FaBrain,
  'bug': FaBug,
  'calculator': FaCalculator,
  'calendar': FaCalendar,
  'check-double': FaCheckDouble,
  'clock': FaClock,
  'cloud-download-alt': FaCloudDownloadAlt,
  'code': FaCode,
  'comments': FaComments,
  'cut': FaCut,
  'database': FaDatabase,
  'edit': FaEdit,
  'envelope': FaEnvelope,
  'exchange-alt': FaExchangeAlt,
  'exclamation-triangle': FaExclamationTriangle,
  'file-archive': FaFileArchive,
  'file-code': FaFileCode,
  'file-export': FaFileExport,
  'file-import': FaFileImport,
  'file-pdf': FaFilePdf,
  'filter': FaFilter,
  'fingerprint': FaFingerprint,
  'grip-lines-vertical': FaGripLinesVertical,
  'hourglass': FaHourglass,
  'image': FaImage,
  'inbox': FaInbox,
  'key': FaKey,
  'link': FaLink,
  'map-signs': FaMapSigns,
  'mouse-pointer': FaMousePointer,
  'network-wired': FaNetworkWired,
  'pause-circle': FaPauseCircle,
  'pen': FaPen,
  'project-diagram': FaProjectDiagram,
  'robot': FaRobot,
  'rss': FaRss,
  'server': FaServer,
  'sign-in-alt': FaSignInAlt,
  'sign-out-alt': FaSignOutAlt,
  'sticky-note': FaStickyNote,
  'sync': FaSync,
  'table': FaTable,
  'tags': FaTags,
  'tasks': FaTasks,
  'terminal': FaTerminal,
  'th-large': FaThLarge,
  'timer': FaTimer,
  'tools': FaTools,
};

/**
 * Map n8n icon color names to hex values
 */
export const FA_COLORS: Record<string, string> = {
  'black': '#1f2937',
  'blue': '#3b82f6',
  'crimson': '#dc143c',
  'dark-blue': '#1e40af',
  'dark-green': '#166534',
  'gray': '#6b7280',
  'green': '#22c55e',
  'light-blue': '#60a5fa',
  'light-green': '#86efac',
  'orange-red': '#ea580c',
  'purple': '#9333ea',
  'red': '#ef4444',
};

/**
 * Default colors for FA icons by category
 */
const DEFAULT_FA_COLOR = '#6b7280'; // gray

/**
 * Get n8n icon URL from GitHub raw CDN
 * @param iconPath Relative path from the nodes directory (e.g., "nodes/Webhook/webhook.svg")
 */
export function getN8nIconUrl(iconPath: string): string {
  // The iconPath comes from transform as "nodes/Webhook/webhook.svg" (with prefix stripped)
  // We need to construct the full GitHub raw URL
  return `https://raw.githubusercontent.com/n8n-io/n8n/master/packages/nodes-base/${iconPath}`;
}

/**
 * Get color value for FA icon
 * @param colorName Color name from n8n (e.g., "green", "blue")
 */
export function getFaIconColor(colorName?: string): string {
  if (!colorName) return DEFAULT_FA_COLOR;
  return FA_COLORS[colorName] || DEFAULT_FA_COLOR;
}

/**
 * Get background color for FA icon based on the icon color
 */
export function getFaIconBgColor(colorName?: string): string {
  const color = colorName || 'gray';
  switch (color) {
    case 'green':
    case 'dark-green':
    case 'light-green':
      return '#dcfce7'; // green-100
    case 'blue':
    case 'dark-blue':
    case 'light-blue':
      return '#dbeafe'; // blue-100
    case 'red':
    case 'crimson':
      return '#fee2e2'; // red-100
    case 'purple':
      return '#f3e8ff'; // purple-100
    case 'orange-red':
      return '#ffedd5'; // orange-100
    default:
      return '#f3f4f6'; // gray-100
  }
}

